import { useContext, useEffect, useState } from "react";
import Joi from "joi";
import { RenderInput } from "../../common/Elements";
import useToast from "hooks/useToast";
import { useTranslation } from "react-i18next";
import SelectInput from "components/SelectInput";
import EditStudentSchema from "./EditStudentSchema";
import Button from "components/Button";
import { selectUpdateItem, IStudent } from "features/Students";
import { IFilterOption, IValidationErrors } from "features/common/Interfaces";
import { useAppSelector } from "app/hooks";
import { useFetchGradesQuery } from "app/services/common";
import { selectClassesByGrade } from "features/Classes";
import {
  IUpdateStudentRequest,
  useUpdateStudentMutation,
} from "app/services/students";
import { IStudentRecordInput } from "../AddStudents/StudentRecord";
import { FireBaseContext } from "contexts/FireBase";
import { Typography } from "@mui/material";

const customStyles = {
  root: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "400px",
    padding: "20px",
    color: "rgb(105 123 140)",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "17px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  form: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    "& > :not(:last-child)": {
      paddingBottom: "10px",
    },
    "& > div": {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-around",
      width: "100%",
    },
  },
};

interface IEditStudentProp {
  name: "name" | "classId" | "studentId" | "email";
  value: string | number;
}
interface IContentProps {
  onSubmit(): void;
}

type FormData = Pick<
  IStudent,
  "name" | "studentId" | "id" | "classId" | "email"
>;

const initForm: FormData = {
  name: "",
  studentId: "",
  id: 0,
  classId: 0,
};

function Content({ onSubmit }: IContentProps) {
  const { logEvent } = useContext(FireBaseContext);
  const [formData, setFormData] = useState<FormData>(initForm);
  const [grades, setGrades] = useState<IFilterOption[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<IFilterOption>();
  const [classList, setClassList] = useState<IFilterOption[]>([]);
  const [errors, setErrors] = useState<IValidationErrors>({});
  const [updateStudent, { isLoading }] = useUpdateStudentMutation();
  const updateItem: IStudent | null = useAppSelector(selectUpdateItem);
  const classesByGrade = useAppSelector((state) =>
    selectClassesByGrade(state, selectedGrade?.value ?? 0)
  );

  const { data: gradeData, isSuccess: gradeIsSuccess } = useFetchGradesQuery();

  const toast = useToast();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (classesByGrade) {
      const list = classesByGrade.map((item) => ({
        label: item.name,
        value: item.id.toString(),
      }));
      setClassList(list);
    }
  }, [classesByGrade]);
  useEffect(() => {
    if (updateItem) {
      setFormData({
        name: updateItem.name,
        studentId: updateItem.studentId,
        classId: updateItem.classId,
        id: updateItem.id,
        email: updateItem.email,
      });
      setSelectedGrade({
        label: updateItem.grade,
        value: updateItem.gradeId as unknown as string,
      });
    }
  }, []);

  useEffect(() => {
    if (gradeIsSuccess) fetchGrades(gradeData);
  }, [gradeIsSuccess]);

  const fetchGrades = (data: typeof gradeData) => {
    try {
      if (data?._statusCode === 200) {
        const list = data._entity?.map((item: any) => ({
          value: item._id,
          label: t(item._code),
        }));
        setGrades(list);
      } else throw data;
    } catch (error) {
      console.log(error);
    }
  };

  const gradeChangeHandler = (event: {
    currentTarget: { name: string; value: string };
  }) => {
    const { name, value } = event.currentTarget;
    setSelectedGrade({ label: name, value });
  };
  const classChangeHandler = (event: {
    currentTarget: { name: string; value: number };
  }) => {
    setFormData({
      ...formData,
      classId: event.currentTarget.value,
    });
  };

  const formVars = {
    name: {
      name: "name",
      label: t("Student"),
      placeholder: t("Student"),
      value: formData?.name,
      autoFocus: true,
    },
    studentId: {
      name: "studentId",
      label: t("StudentId"),
      placeholder: t("StudentId"),
      value: formData?.studentId,
    },
    email: {
      name: "email",
      label: t("Email"),
      placeholder: t("Email"),
      value: formData.email,
    },
    grade: {
      name: "grade",
      label: t("Select Grade"),
      valueList: grades,
      getOption: gradeChangeHandler,
      defaultValue: selectedGrade?.value,
    },
    className: {
      name: "className",
      label: t("Class Name"),
      defaultValue: formData?.classId,
      valueList: classList,
      getOption: classChangeHandler,
    },
  };

  const editHandler = async () => {
    try {
      let payload = {
        _id: formData.id,
        _classId: formData.classId,
        _fullName: formData.name.trim(),
        _emailAddress: formData.email,
        _studentSchoolUid:
          formData.studentId !== updateItem?.studentId?.trim()
            ? formData.studentId
            : null,
      } as IUpdateStudentRequest;
      const data = await updateStudent(payload).unwrap();
      if (data?._statusCode === 200) {
        toast({
          label: t("success"),
          message: t("Student Updated"),
          rtl: true,
          type: "success",
        });
        onSubmit();
      } else if (data?._statusCode === 572) {
        toast({
          label: t("error"),
          message: t("studentId already exists"),
          rtl: true,
          type: "error",
          toastId: "studentUpdate",
        });
        setErrors({ ...errors, studentId: t("studentId already exists") });
      } else throw data;
    } catch (error) {
      console.log({ error });
      onSubmit();
    }
    logEvent("ClickOnConfirmEditStudent");
  };

  const changeHandler = (event: {
    currentTarget: {
      name: string;
      value: string | number;
    };
  }) => {
    const { currentTarget: input } = event as IStudentRecordInput;
    const errorsObject: IValidationErrors = { ...errors };
    if (input.name === "email" && input.value.length > 0)
      input.value = input.value.trim();
    const errorMesssage = validateProperty(input as IEditStudentProp);
    if (errorMesssage) errorsObject[input.name] = errorMesssage;
    else delete errorsObject[input.name];
    setErrors(errorsObject);
    setFormData({ ...formData, [input.name]: input.value });
  };

  const validateProperty = ({ name, value }: IEditStudentProp) => {
    const obj = { [name]: value };
    const propertySchema = {
      [name]: EditStudentSchema[name],
    };
    const { error } = Joi.object(propertySchema).validate(obj);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.object(EditStudentSchema).validate(
      {
        name: formData?.name,
        studentId: formData?.studentId,
        classId: formData?.classId,
      },
      options
    );
    let errorsObject = { ...errors };
    if (error)
      for (let item of error.details) errorsObject[item.path[0]] = item.message;
    return errorsObject;
  };

  const isFormNotValid = Object.keys(validate()).length > 0;

  return (
    <Typography component="div" sx={customStyles.root}>
      <Typography
        component="div"
        sx={{ ...customStyles.form, direction: i18n.dir() }}
      >
        <div>
          <div style={{ width: "250px" }}>
            {RenderInput({
              ...formVars.name,
              errors: t(errors.name),
              changeHandler,
              styles: { padding: "0 0 30px", width: "250px" },
            })}
          </div>
          <div style={{ width: "250px" }}>
            <SelectInput
              {...formVars.grade}
              placeholder={t("Select")}
              style={{ container: { zIndex: 202 } }}
            />
          </div>
        </div>
        <div>
          <div style={{ width: "250px" }}>
            {RenderInput({
              ...formVars.studentId,
              errors: t(errors.studentId),
              changeHandler,
              styles: { padding: "0 0 30px", width: "250px" },
            })}
          </div>
          <div style={{ width: "250px" }}>
            <SelectInput
              {...formVars.className}
              placeholder={t("Select")}
              dropDownHeight="125px"
              searchable
            />
          </div>
        </div>
        <div>
          <div style={{ width: "350px" }}>
            {RenderInput({
              ...formVars.email,
              errors: t(errors.email),
              changeHandler,
              styles: { padding: 0, width: "350px" },
            })}
          </div>
        </div>

        <Button
          label={t("Edit")}
          action={editHandler}
          styles={{ width: "300px", margin: "20px 0" }}
          disable={isFormNotValid || isLoading}
        />
      </Typography>
    </Typography>
  );
}

export default Content;
